import { Link } from "react-router-dom";
import { Clock, MapPin, Users } from "lucide-react";
import MatchScoreBadge from "@/components/MatchScoreBadge";
import { formatTimeFromNow, getActivityType, getVibeLabel, getCommunity } from "@/lib/mockData";

const ActivityCard = ({ activity, index = 0 }) => {
  const type = getActivityType(activity.type);
  const community = activity.community ? getCommunity(activity.community) : null;
  const isFull = (activity.participants || []).length >= activity.max_participants;
  return (
    <Link
      to={`/activity/${activity.id}`}
      className="block brutal-card overflow-hidden fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`activity-card-${activity.id}`}
    >
      {/* Image header */}
      <div className="relative h-40 overflow-hidden border-b-2 border-[#0A0A0A]">
        <img src={activity.image || "https://images.unsplash.com/photo-1517701986616-711a9d625afe?w=800&q=80"} alt={activity.title} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
          <span className="brutal-border bg-white rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1">
            <span>{type.emoji}</span>
            <span className="uppercase tracking-wide">{type.label}</span>
          </span>
          {activity.spontaneous && (
            <span className="brutal-border bg-[#FF6A2C] text-white rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide">
              Spontaneous
            </span>
          )}
          {community && (
            <span
              className="brutal-border rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide"
              style={{ background: community.color }}
              data-testid={`activity-community-${activity.id}`}
            >
              {community.emoji} {community.label}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <MatchScoreBadge score={activity.match_score} />
        </div>
        {activity.deal && (
          <div
            className="absolute -bottom-2 right-4 brutal-border px-3 py-1 rounded-md font-bold text-xs uppercase rotate-[-3deg]"
            style={{ background: activity.deal.color, boxShadow: "2px 2px 0px 0px #0A0A0A" }}
          >
            {activity.deal.label}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <img
            src={activity.host.avatar_url || activity.host.avatar || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=80"}
            alt={activity.host.name}
            className="w-10 h-10 rounded-full brutal-border object-cover -mt-9 brutal-shadow-sm bg-white"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-lg leading-tight">{activity.title}</h3>
            <p className="text-xs text-[#555555] mt-0.5">
              {activity.host.name}{activity.host.age ? `, ${activity.host.age}` : ""} • {getVibeLabel(activity.vibe)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <Stat icon={Clock} value={formatTimeFromNow(activity.time_iso)} />
          <Stat icon={MapPin} value={`${activity.distance_km ?? 0} km`} />
          <Stat
            icon={Users}
            value={`${(activity.participants || []).length}/${activity.max_participants}`}
            highlight={isFull}
          />
        </div>

        {/* Match reasons */}
        <div className="flex flex-wrap gap-1.5">
          {(activity.match_reasons || []).slice(0, 3).map((r, i) => (
            <span
              key={i}
              className="text-xs px-2 py-0.5 rounded-full bg-[#F8F9FA] border border-[#E0E0E0] text-[#555555]"
            >
              {r}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};

const Stat = ({ icon: Icon, value, highlight }) => (
  <div
    className={`flex items-center gap-1.5 ${highlight ? "bg-[#FF6A2C] text-white" : "bg-[#F8F9FA]"} brutal-border rounded-md px-2 py-1.5`}
  >
    <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
    <span className="text-xs font-bold truncate">{value}</span>
  </div>
);

export default ActivityCard;
